import { useRef, useLayoutEffect, useState } from 'react';

/**
 * useFitText - A React hook to automatically scale down the font size of text so it fits within its container.
 *
 * This hook is useful for situations where you want to prevent long words or text from overflowing or breaking awkwardly,
 * by dynamically reducing the font size until the text fits within the available width.
 *
 * @param {number} minFontSize - The minimum font size (in px) to scale down to. Default is 10.
 * @param {number} maxFontSize - The maximum font size (in px) to start from. Default is 16.
 * @returns {{ ref: React.RefObject<HTMLSpanElement>, fontSize: number }}
 *   - ref: Attach this to the span or element containing the text you want to fit.
 *   - fontSize: The calculated font size that fits the text within the container.
 *
 * @example
 *   const { ref, fontSize } = useFitText(10, 16);
 *   return <span ref={ref} style={{ fontSize }}>{text}</span>;
 */
export default function useFitText(minFontSize = 10, maxFontSize = 16) {
  const ref = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    let currentFontSize = maxFontSize;
    el.style.fontSize = `${currentFontSize}px`;

    // Reduce font size until it fits or reaches minFontSize
    while (el.scrollWidth > el.offsetWidth && currentFontSize > minFontSize) {
      currentFontSize -= 1;
      el.style.fontSize = `${currentFontSize}px`;
    }
    setFontSize(currentFontSize);
  }, [minFontSize, maxFontSize, ref.current?.textContent]);

  return { ref, fontSize };
}
